/**
 * usePermissions Hook
 * Provides easy access to permission checks in React components
 */

import {
  useAuthStore,
  selectUserContext,
  selectIsOrganizationApproved,
  selectIsOrganizationPending,
} from './auth-store'

// Permission types
export const Permissions = {
  RFQ_VIEW: 'RFQ_VIEW',
  RFQ_CREATE: 'RFQ_CREATE',
  RFQ_EDIT: 'RFQ_EDIT',
  RFQ_APPROVE: 'RFQ_APPROVE',
  RFQ_DELETE: 'RFQ_DELETE',
  QUOTATION_VIEW: 'QUOTATION_VIEW',
  QUOTATION_RESPOND: 'QUOTATION_RESPOND',
  MEMBER_VIEW: 'MEMBER_VIEW',
  MEMBER_INVITE: 'MEMBER_INVITE',
  MEMBER_MANAGE: 'MEMBER_MANAGE',
  MEMBER_REMOVE: 'MEMBER_REMOVE',
  ORG_SETTINGS_VIEW: 'ORG_SETTINGS_VIEW',
  ORG_SETTINGS_EDIT: 'ORG_SETTINGS_EDIT',
  BILLING_VIEW: 'BILLING_VIEW',
  BILLING_MANAGE: 'BILLING_MANAGE',
} as const

export type Permission = (typeof Permissions)[keyof typeof Permissions]

/**
 * Hook for checking user permissions
 *
 * @example
 * ```tsx
 * const { hasPermission, canCreateRfq, isApproved } = usePermissions()
 *
 * if (hasPermission('RFQ_CREATE')) {
 *   // Show create button
 * }
 * ```
 */
export function usePermissions() {
  const userContext = useAuthStore(selectUserContext)
  const isApproved = useAuthStore(selectIsOrganizationApproved)
  const isPending = useAuthStore(selectIsOrganizationPending)

  const permissions = userContext?.permissions ?? []

  /**
   * Check if user has a specific permission
   */
  const hasPermission = (permission: Permission | string): boolean => {
    return permissions.includes(permission)
  }

  /**
   * Check if user has any of the specified permissions
   */
  const hasAnyPermission = (perms: (Permission | string)[]): boolean => {
    return perms.some(p => permissions.includes(p))
  }

  /**
   * Check if user has all of the specified permissions
   */
  const hasAllPermissions = (perms: (Permission | string)[]): boolean => {
    return perms.every(p => permissions.includes(p))
  }

  return {
    // Raw data
    permissions,
    userContext,

    // Organization status (case-insensitive checks - backend returns lowercase)
    isApproved,
    isPending,
    isSuspended: userContext?.approvalStatus?.toUpperCase() === 'SUSPENDED',
    isRejected: userContext?.approvalStatus?.toUpperCase() === 'REJECTED',
    approvalStatus: userContext?.approvalStatus ?? null,

    // Organization info
    organizationId: userContext?.organizationId ?? null,
    organizationName: userContext?.organizationName ?? null,
    orgRole: userContext?.orgRole ?? null,

    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Convenience permission checks
    canViewRfqs: hasPermission(Permissions.RFQ_VIEW),
    canCreateRfq: hasPermission(Permissions.RFQ_CREATE),
    canEditRfq: hasPermission(Permissions.RFQ_EDIT),
    canApproveRfq: hasPermission(Permissions.RFQ_APPROVE),
    canDeleteRfq: hasPermission(Permissions.RFQ_DELETE),

    canViewQuotations: hasPermission(Permissions.QUOTATION_VIEW),
    canRespondToQuotations: hasPermission(Permissions.QUOTATION_RESPOND),

    canViewMembers: hasPermission(Permissions.MEMBER_VIEW),
    canInviteMembers: hasPermission(Permissions.MEMBER_INVITE),
    canManageMembers: hasPermission(Permissions.MEMBER_MANAGE),
    canRemoveMembers: hasPermission(Permissions.MEMBER_REMOVE),

    canViewOrgSettings: hasPermission(Permissions.ORG_SETTINGS_VIEW),
    canEditOrgSettings: hasPermission(Permissions.ORG_SETTINGS_EDIT),

    canViewBilling: hasPermission(Permissions.BILLING_VIEW),
    canManageBilling: hasPermission(Permissions.BILLING_MANAGE),

    // Role checks
    isOwner: userContext?.orgRole === 'OWNER',
    isAdmin: userContext?.orgRole === 'ADMIN' || userContext?.orgRole === 'OWNER',
    isManager: userContext?.orgRole === 'MANAGER',
    isViewer: userContext?.orgRole === 'VIEWER',

    // Platform admin check (backoffice users)
    isPlatformAdmin:
      userContext?.memberType?.toUpperCase() === 'BACKOFFICE' ||
      userContext?.orgRole?.toUpperCase() === 'BACKOFFICE' ||
      permissions.includes('PLATFORM_ADMIN') ||
      permissions.includes('wasilah-admin'),
  }
}
